import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { getAppMedia, mediaUrl } from "@/lib/api";

const AppGallery = ({ slug }) => {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null); // index of lightbox-open item

  useEffect(() => {
    if (!slug) return;
    getAppMedia(slug).then(setItems).catch(() => setItems([]));
  }, [slug]);

  useEffect(() => {
    if (active === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setActive(null);
      if (e.key === "ArrowRight") setActive((i) => (i + 1) % items.length);
      if (e.key === "ArrowLeft") setActive((i) => (i - 1 + items.length) % items.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, items.length]);

  if (items.length === 0) return null;

  const featured = items[0];
  const rest = items.slice(1, 5);

  return (
    <section className="hac-container py-12" data-testid="app-gallery">
      <div className="overline mb-3 text-[#2563EB]">Screenshots & video</div>
      <h2 className="font-heading text-2xl md:text-3xl text-[#0F172A] mb-6">See it in action</h2>

      <div className="grid md:grid-cols-2 gap-3">
        {/* Featured media (left, large) */}
        <button
          onClick={() => setActive(0)}
          className="group relative aspect-[4/3] bg-[#F1F5F9] rounded-2xl overflow-hidden border border-[#E2E8F0] text-left"
          data-testid="gallery-featured"
        >
          {featured.type === "image" ? (
            <img src={mediaUrl(featured.url)} alt={featured.caption || ""} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform" />
          ) : featured.embed_url ? (
            <iframe
              src={featured.embed_url}
              title={featured.caption || "Video"}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
            />
          ) : null}
          {(featured.caption || featured.feature_label) && (
            <div className="absolute bottom-0 inset-x-0 p-4 bg-gradient-to-t from-black/60 to-transparent text-white">
              {featured.feature_label && (
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/80 mb-0.5">{featured.feature_label}</div>
              )}
              {featured.caption && <div className="text-sm font-semibold">{featured.caption}</div>}
            </div>
          )}
        </button>

        {/* Grid of the rest */}
        <div className="grid grid-cols-2 gap-3">
          {rest.map((m, i) => {
            const isLastVisible = i === rest.length - 1;
            const hasOverflow = items.length > 5;
            const overflowCount = items.length - 5;
            const showOverflow = isLastVisible && hasOverflow;
            return (
              <button
                key={m.id}
                onClick={() => setActive(showOverflow ? 5 : i + 1)}
                className="group relative aspect-[4/3] bg-[#F1F5F9] rounded-xl overflow-hidden border border-[#E2E8F0] text-left"
                data-testid={showOverflow ? "gallery-more" : `gallery-thumb-${m.id}`}
              >
                {m.type === "image" ? (
                  <img src={mediaUrl(m.url)} alt={m.caption || ""} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-[#0F172A] text-white">
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">▶</div>
                  </div>
                )}
                {m.feature_label && !showOverflow && (
                  <div className="absolute top-2 left-2 bg-white/95 text-[#0F172A] text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full">
                    {m.feature_label}
                  </div>
                )}
                {showOverflow && (
                  <div className="absolute inset-0 bg-black/55 backdrop-blur-[1px] flex flex-col items-center justify-center text-white">
                    <div className="font-heading text-2xl md:text-3xl leading-none">+{overflowCount}</div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-white/80 mt-1">more</div>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Lightbox */}
      {active !== null && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setActive(null)} data-testid="gallery-lightbox">
          <button onClick={(e) => { e.stopPropagation(); setActive(null); }} className="absolute top-4 right-4 text-white/80 hover:text-white p-2" data-testid="gallery-close">
            <X size={20} />
          </button>
          {items.length > 1 && (
            <>
              <button onClick={(e) => { e.stopPropagation(); setActive((i) => (i - 1 + items.length) % items.length); }} className="absolute left-4 text-white/80 hover:text-white p-3" data-testid="gallery-prev">
                <ChevronLeft size={28} />
              </button>
              <button onClick={(e) => { e.stopPropagation(); setActive((i) => (i + 1) % items.length); }} className="absolute right-4 text-white/80 hover:text-white p-3" data-testid="gallery-next">
                <ChevronRight size={28} />
              </button>
            </>
          )}
          <div className="max-w-5xl w-full max-h-[85vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            {items[active].type === "image" ? (
              <img src={mediaUrl(items[active].url)} alt="" className="max-h-[80vh] object-contain rounded-lg" />
            ) : items[active].embed_url ? (
              <div className="w-full aspect-video">
                <iframe src={items[active].embed_url} title="Video" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen className="w-full h-full rounded-lg" />
              </div>
            ) : null}
            {(items[active].caption || items[active].feature_label) && (
              <div className="text-white text-center mt-3">
                {items[active].feature_label && (
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/60 mb-0.5">{items[active].feature_label}</div>
                )}
                {items[active].caption && <div className="text-sm">{items[active].caption}</div>}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default AppGallery;
